import { Component, Input, signal, Output, EventEmitter, computed, forwardRef, ViewChild, ElementRef, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { IconGlassComponent } from '../icon/icon-glass.component';
import { CheckboxVariant, CheckboxSize } from '../models/checkbox.model';

const checkboxVariants = cva({
  base: [
    'relative inline-block rounded-xl border-2 transition-all duration-300 ease-out cursor-pointer',
    'backdrop-blur-md transform-gpu flex items-center justify-center',
    'hover:scale-105 active:scale-95',
    'focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-2 focus:outline-none',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
    // Glass morphism base
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-60 before:rounded-xl before:pointer-events-none',
    'before:transition-opacity before:duration-300',
    // Touch target: invisible pseudo-element extends clickable area to min 44px
    'after:content-[""] after:absolute after:-inset-3 after:rounded-lg',
  ].join(' '),
  variants: {
    variant: {
      default: [
        // Unchecked state
        'border-gray-300/60 bg-white/25',
        'hover:border-gray-300/80 hover:bg-white/35',
        'dark:border-gray-600/60 dark:bg-gray-800/25',
        'dark:hover:border-gray-600/80 dark:hover:bg-gray-800/35',
        // Checked state (applied via checked variant)
      ].join(' '),
      elevated: [
        // Unchecked state
        'border-primary-200/60 bg-white/30 shadow-lg shadow-primary-500/10',
        'hover:border-primary-200/80 hover:bg-white/40 hover:shadow-xl hover:shadow-primary-500/15',
        'dark:border-primary-400/60 dark:bg-gray-800/30 dark:shadow-lg dark:shadow-primary-500/15',
        'dark:hover:border-primary-400/80 dark:hover:bg-gray-800/40',
      ].join(' '),
      premium: [
        // Ultra-premium for e-commerce
        'border-primary-300/50 bg-linear-to-br from-white/20 via-white/15 to-primary-50/20',
        'shadow-xl shadow-primary-500/20',
        'hover:border-primary-300/70 hover:from-white/25 hover:via-white/20 hover:to-primary-50/30',
        'hover:shadow-2xl hover:shadow-primary-500/30',
        'dark:border-primary-400/40 dark:from-white/15 dark:via-white/10 dark:to-primary-900/20',
        'dark:shadow-xl dark:shadow-primary-500/25',
        'dark:hover:border-primary-400/60 dark:hover:from-white/20',
      ].join(' '),
      minimal: [
        'border-gray-200/50 bg-white/20',
        'hover:border-gray-200/70 hover:bg-white/30',
        'dark:border-gray-600/50 dark:bg-gray-800/20',
        'dark:hover:border-gray-600/70 dark:hover:bg-gray-800/30',
      ].join(' '),
    },
    size: {
      sm: 'w-5 h-5',
      md: 'w-6 h-6',
      lg: 'w-8 h-8',
      xl: 'w-10 h-10',
    },
    checked: {
      true: [
        // Checked state styling
        'border-primary-500 bg-primary-500/30',
        'shadow-lg shadow-primary-500/50',
        'dark:border-primary-400 dark:bg-primary-400/35',
        'dark:shadow-lg dark:shadow-primary-500/60',
      ].join(' '),
      false: '',
    },
    indeterminate: {
      true: [
        'border-primary-400/60 bg-primary-400/15',
        'shadow-md shadow-primary-500/20',
        'dark:border-primary-300/70 dark:bg-primary-300/20',
      ].join(' '),
      false: '',
    },
    state: {
      default: '',
      error: 'border-red-400/50 focus:border-red-500/60 focus:ring-red-500/50',
      success: 'border-cyan-400/50 focus:border-cyan-500/60 focus:ring-cyan-500/50',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    checked: 'false',
    indeterminate: 'false',
    state: 'default',
  },
});

const labelVariants = cva({
  base: [
    'cursor-pointer transition-all duration-200 ease-out',
    'hover:text-gray-900 dark:hover:text-white',
    'select-none',
  ].join(' '),
  variants: {
    size: {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
    },
    disabled: {
      true: 'opacity-50 cursor-not-allowed',
      false: 'text-gray-700 dark:text-gray-200',
    },
  },
  defaultVariants: {
    size: 'md',
    disabled: 'false',
  },
});

@Component({
  selector: 'ui-checkbox-glass',
  standalone: true,
  imports: [IconGlassComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxGlassComponent),
      multi: true,
    },
  ],
  templateUrl: './checkbox-glass.component.html',
})
export class CheckboxGlassComponent implements ControlValueAccessor, AfterViewInit, OnChanges {
  @ViewChild('nativeCheckbox', { static: false }) private checkboxRef!: ElementRef<HTMLInputElement>;
  @Input() label: string = '';
  @Input() description: string = '';
  @Input() variant: CheckboxVariant = 'default';
  @Input() size: CheckboxSize = 'md';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() indeterminate: boolean = false;
  @Input() errorMessage: string = '';
  @Input() successMessage: string = '';
  @Input() themeSection: ThemeSection = 'global';

  @Output() checkedChange = new EventEmitter<boolean>();
  @Output() focus = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();

  protected checkboxId = `checkbox-${Math.random().toString(36).substring(2, 11)}`;
  protected checked = signal(false);

  // ControlValueAccessor
  private onChange = (_value: boolean) => {};
  private onTouched = () => {};

  ngAfterViewInit(): void {
    this.syncIndeterminate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['indeterminate']) {
      this.syncIndeterminate();
    }
  }

  private syncIndeterminate(): void {
    if (this.checkboxRef) {
      this.checkboxRef.nativeElement.indeterminate = this.indeterminate;
    }
  }

  protected iconSize = computed(() => {
    const sizeMap = {
      sm: 'sm' as const,
      md: 'sm' as const,
      lg: 'md' as const,
      xl: 'lg' as const,
    };
    return sizeMap[this.size];
  });

  protected computedCheckboxClasses = computed(() => {
    const state = this.errorMessage ? 'error' : this.successMessage ? 'success' : 'default';
    return checkboxVariants({
      variant: this.variant,
      size: this.size,
      checked: this.checked() ? 'true' : 'false',
      indeterminate: this.indeterminate ? 'true' : 'false',
      state,
    });
  });

  protected computedLabelClasses = computed(() => {
    return labelVariants({
      size: this.size,
      disabled: this.disabled ? 'true' : 'false',
    });
  });

  protected handleChange(event: Event): void {
    if (this.disabled) return;

    const target = event.target as HTMLInputElement;
    const newValue = target.checked;

    this.checked.set(newValue);
    this.onChange(newValue);
    this.checkedChange.emit(newValue);

    // Clear indeterminate state when user clicks
    if (this.indeterminate) {
      this.indeterminate = false;
      this.syncIndeterminate();
    }
  }

  protected handleKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.toggle();
    }
  }

  protected onFocus(): void {
    this.focus.emit();
  }

  protected onBlur(): void {
    this.onTouched();
    this.blur.emit();
  }

  toggle(): void {
    if (this.disabled) return;

    const newValue = !this.checked();
    this.checked.set(newValue);
    this.onChange(newValue);
    this.checkedChange.emit(newValue);

    // Clear indeterminate state when toggled programmatically
    if (this.indeterminate) {
      this.indeterminate = false;
      this.syncIndeterminate();
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: boolean): void {
    this.checked.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

/**
 * Usage Examples:
 *
 * Basic checkbox:
 * <ui-checkbox-glass label="I agree to the terms"></ui-checkbox-glass>
 *
 * Premium e-commerce checkbox:
 * <ui-checkbox-glass
 *   label="Add gift wrapping"
 *   description="Beautiful gift wrapping for $5.99"
 *   variant="premium"
 *   size="lg"
 * ></ui-checkbox-glass>
 *
 * Form integration:
 * <ui-checkbox-glass
 *   label="Subscribe to newsletter"
 *   [formControl]="newsletterControl"
 *   [errorMessage]="getErrorMessage('newsletter')"
 * ></ui-checkbox-glass>
 *
 * Indeterminate state (for select all):
 * <ui-checkbox-glass
 *   label="Select all products"
 *   [indeterminate]="someSelected && !allSelected"
 *   [checked]="allSelected"
 *   (checkedChange)="toggleSelectAll()"
 * ></ui-checkbox-glass>
 *
 * Different variants:
 * <ui-checkbox-glass variant="elevated" label="Premium option"></ui-checkbox-glass>
 * <ui-checkbox-glass variant="minimal" label="Simple choice"></ui-checkbox-glass>
 */
